// Centralised data-testid catalog - keep in kebab-case.
export const TID = {
  // Landing
  landingHero: "landing-hero",
  landingCtaTop: "landing-cta-top",
  landingCtaBottom: "landing-cta-bottom",
  landingSignInLink: "landing-signin-link",

  // Auth
  loginGoogleBtn: "login-google-btn",
  loginTitle: "login-title",
  authCallbackLoading: "auth-callback-loading",

  // App shell
  sidebar: "app-sidebar",
  sidebarBrand: "sidebar-brand",
  sidebarLinkFindCustomers: "sidebar-find-customers",
  sidebarLinkMarket: "sidebar-market-analysis",
  sidebarLinkPartners: "sidebar-partners",
  sidebarLinkSavedLeads: "sidebar-saved-leads",
  sidebarLinkBrand: "sidebar-brand-foundation",
  sidebarLinkSettings: "sidebar-settings",
  sidebarLinkBilling: "sidebar-billing",
  topbarUsageBadge: "topbar-usage-badge",
  topbarWorkspaceName: "topbar-workspace-name",
  topbarUserMenu: "topbar-user-menu",
  topbarLogout: "topbar-logout",
  topbarMobileMenu: "topbar-mobile-menu",

  // Onboarding
  onboardingUrlInput: "onboarding-url-input",
  onboardingSubmit: "onboarding-submit",
  onboardingGenerating: "onboarding-generating",
  onboardingConfirm: "onboarding-confirm",
  onboardingBrandName: "onboarding-brand-name",

  // Onboarding interview (three questions asked during the crawl).
  // Drive the step off `data-interview-state` on interviewPanel (pending |
  // resolved) and the live question off `data-question-id` on
  // interviewQuestion (who_buys | buyer_words | blind_spots).
  interviewPanel: "onboarding-interview",
  interviewQuestion: "onboarding-interview-question",
  interviewInput: "onboarding-interview-input",
  interviewSend: "onboarding-interview-send",
  // Q3 ONLY, and it is "Nothing to add" — an answer, not a skip. Q1/Q2 render no
  // second button at all (the interview is mandatory as of 20 Jul 2026); the id
  // string is unchanged so existing selectors still resolve. Asserting it is
  // ABSENT on who_buys / buyer_words is the regression test for skip creeping back.
  interviewSkip: "onboarding-interview-skip",
  // The one gentle re-ask on a thin answer, and the empty-box line. Same element:
  // read `data-probe` — "probe" | "empty" | "" (nothing showing). Never an error
  // state; assert no red/`text-lm-secondary` here.
  interviewProbe: "onboarding-interview-probe",
  interviewCount: "onboarding-interview-count",
  interviewAck: "onboarding-interview-ack",
  interviewDone: "onboarding-interview-done",   // `data-variant`: early | done
  // Progress while the interview is up. THE honest-progress assertion: read
  // `data-waiting` on brandGenWaiting — "true" means nothing is running and the
  // bar must not move, "false" means the crawl is genuinely in flight.
  brandGenWaiting: "brand-gen-waiting",
  brandGenWaitingBar: "brand-gen-waiting-progress",

  // Find customers
  scanTriggerBtn: "scan-trigger-btn",
  scanRunningLoader: "scan-running-loader",
  costConfirmDialog: "cost-confirm-dialog",
  costConfirmProceed: "cost-confirm-proceed",
  costConfirmCancel: "cost-confirm-cancel",
  filterPlatform: "filter-platform",
  filterPainToggle: "filter-pain-toggle",
  filterIntelToggle: "filter-intel-toggle",
  filterScore: "filter-score",
  leadCard: "lead-card",
  leadSaveBtn: "lead-save-btn",
  leadDismissBtn: "lead-dismiss-btn",
  leadDraftReplyBtn: "lead-draft-reply-btn",
  leadOpenLinkBtn: "lead-open-link-btn",
  emptyStateFindCustomers: "empty-state-find-customers",
  errorStateFindCustomers: "error-state-find-customers",
  // Empty-scan explanation (persistent, replaces the old expiring toast).
  // Drive the three states off `data-empty-reason` on emptyScanPanel:
  // all_seen | all_filtered | no_matches. The charged-vs-refunded variant is
  // `data-charged` on emptyScanMoney.
  emptyScanPanel: "empty-scan-panel",
  emptyScanHeadline: "empty-scan-headline",
  emptyScanSupport: "empty-scan-support",
  emptyScanNext: "empty-scan-next",
  emptyScanMoney: "empty-scan-money",

  // Saved Leads
  savedLeadsList: "saved-leads-list",
  savedLeadCard: "saved-lead-card",
  savedLeadStatusSelect: "saved-lead-status-select",
  savedLeadNotesInput: "saved-lead-notes-input",
  savedLeadDelete: "saved-lead-delete",

  // Settings
  settingsWorkspaceNameInput: "settings-workspace-name-input",
  settingsSaveBtn: "settings-save-btn",

  // Billing
  billingPlanCard: "billing-plan-card",
  billingChoosePlanBtn: "billing-choose-plan-btn",
  billingTransactionsRow: "billing-tx-row",
  billingCurrencyToggle: "billing-currency-toggle",
  billingCadenceToggle: "billing-cadence-toggle",
  billingSubCard: "billing-sub-card",
  billingLtdCard: "billing-ltd-card",
  billingTopupCard: "billing-topup-card",
  billingSubscribeBtn: "billing-subscribe-btn",
  billingLtdBtn: "billing-ltd-btn",
  billingTopupBtn: "billing-topup-btn",
  billingCreditLedger: "billing-credit-ledger",
  billingCreditLedgerRow: "billing-credit-ledger-row",
  billingReconcileBtn: "billing-reconcile-btn",

  // Workspace failure & recovery (the one global banner, §4).
  // Drive states off `data-ws-state` on wsBanner: retrying | unreachable |
  // offline; the healed banner is its own node.
  wsBanner: "ws-banner",
  wsBannerTitle: "ws-banner-title",
  wsBannerLine2: "ws-banner-line2",
  wsBannerAttempt: "ws-banner-attempt",     // S3a — only while a request is OPEN
  wsBannerCountdown: "ws-banner-countdown", // S3b — only between attempts
  wsBannerWatching: "ws-banner-watching",   // polling vs paused retraction
  wsBannerLastResort: "ws-banner-lastresort",
  wsBannerRef: "ws-banner-ref",
  wsBannerRetry: "ws-banner-retry",
  wsBannerHealed: "ws-banner-healed",       // S5, 2.5s exactly
  wsNameStalled: "workspace-switcher-name-stalled",
  // The "Paused" gate variant (aria-busy="false") at each site that already had
  // a "Checking…" pending variant.
  scanUnknownBtn: "scan-unknown-btn",
  scanMetaUnknown: "scan-meta-unknown",
  savedLeadsUnknown: "saved-leads-unknown",
  billingUsageUnknown: "billing-usage-unknown",
  dashboardLoadFailed: "dashboard-load-failed",
  dashboardLoadRetry: "dashboard-load-retry",

  // Coming soon
  comingSoonHero: "coming-soon-hero",
};
